import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios_instance from '../api/axios_instance';
import { API_URL } from '../config';
import {
  Loader2, Package, Trash2, PlusCircle,
  Languages, X, CheckCircle2, Star, Flame, Sparkles,
  AlertCircle, ArrowRight, Save, Image as ImageIcon,
  RotateCw, Check, Upload, GripVertical, Download
} from 'lucide-react';

const EditProduct = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [deletingImg, setDeletingImg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const dragIndexRef = useRef(null);
  
  const [variants, setVariants] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [initialState, setInitialState] = useState(null);

  const [formData, setFormData] = useState({
    _id: '',
    nameAr: '', nameEn: '', nameFr: '',
    descAr: '', descEn: '', descFr: '',
    detailedAr: '', detailedEn: '', detailedFr: '',
    category: [],
    subCategory: [],
    isNewProduct: false,
    isMostSold: false,
    featured: false,
    isPack: false,
    status: 'available'
  });

  const backendURL = API_URL;
  const inputStyle = "w-full p-2.5 border border-gray-300 rounded-lg outline-none bg-white text-sm text-gray-900 focus:border-emerald-500 transition-colors placeholder:text-gray-400";
  const labelStyle = "text-[11px] font-black text-gray-500 mb-1.5 block uppercase tracking-wider";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          axios_instance.get(`/api/products/${slug}`),
          axios_instance.get('/api/categories')
        ]);

        const p = prodRes.data;
        setCategories(catRes.data);
        
        const mappedData = {
          _id: p._id,
          nameAr: p.name?.ar || '', nameEn: p.name?.en || '', nameFr: p.name?.fr || '',
          descAr: p.description?.ar || '', descEn: p.description?.en || '', descFr: p.description?.fr || '',
          detailedAr: p.detailedDescription?.ar || '', detailedEn: p.detailedDescription?.en || '', detailedFr: p.detailedDescription?.fr || '',
          category: Array.isArray(p.category) ? p.category.map(c => c._id || c) : [p.category?._id || p.category],
          subCategory: Array.isArray(p.subCategory) ? p.subCategory.map(s => s._id || s) : [],
          isNewProduct: p.isNewProduct || false,
          isMostSold: p.isMostSold || false,
          featured: p.featured || p.isFeatured || false,
          isPack: p.isPack || false,
          status: p.status || 'available'
        };

        const mappedVariants = (p.variants || []).map(v => ({
          grammage: String(v.grammage || ''),
          price: String(v.price || ''),
          discountPrice: String(v.discountPrice || ''),
          isOnSale: Boolean(v.isOnSale),
          stock: Number(v.stock || 0)
        }));

        setFormData(mappedData);
        setVariants(mappedVariants);
        setExistingImages(p.images || []);
        
        setInitialState({
          form: JSON.stringify(mappedData),
          variants: JSON.stringify(mappedVariants),
          images: JSON.stringify(p.images || [])
        });

      } catch (err) {
        showToast("خطأ في تحميل بيانات المنتج", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    const filtered = categories.filter(c => 
      c.parentCategory && formData.category.includes(c.parentCategory._id || c.parentCategory)
    );
    setSubCategories(filtered);
  }, [formData.category, categories]);

  const isFormValid = useMemo(() => {
    const basicInfoValid = formData.nameAr.trim() !== '' && formData.nameEn.trim() !== '' && formData.category.length > 0;
    const variantsValid = variants.length > 0 && variants.every(v => v.grammage.trim() !== '' && String(v.price).trim() !== '');
    return basicInfoValid && variantsValid;
  }, [formData, variants]);

  const hasChanges = useMemo(() => {
    if (!initialState) return false;
    const currentFormStr = JSON.stringify(formData);
    const currentVariantsStr = JSON.stringify(variants.map(v => ({
      grammage: String(v.grammage || '').trim(),
      price: String(v.price || '').trim(),
      discountPrice: String(v.discountPrice || '').trim(),
      isOnSale: Boolean(v.isOnSale),
      stock: Number(v.stock || 0)
    })));
    const imagesChanged = JSON.stringify(existingImages) !== initialState.images;
    return currentFormStr !== initialState.form || currentVariantsStr !== initialState.variants || imagesChanged;
  }, [formData, variants, existingImages, initialState]);

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 5000);
  };

  const toggleArray = (key, id) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].includes(id) ? prev[key].filter(i => i !== id) : [...prev[key], id]
    }));
  };

  const handleDeleteImage = async (imgToDelete) => {
    setDeletingImg(imgToDelete);
    try {
      const res = await axios_instance.put(`/api/products/${formData._id}/delete-image`, {
        imagePath: imgToDelete
      });
      setExistingImages(res.data.images);
      showToast("تم حذف الصورة بنجاح");
      setConfirmDelete(null);
    } catch (err) {
      showToast(err.response?.data?.message || "خطأ في حذف الصورة", "error");
    } finally {
      setDeletingImg(null);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Strict validation: max 4 images
    if (existingImages.length + files.length > 4) {
      showToast("لا يمكن إضافة أكثر من 4 صور للمنتج", "error");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    files.forEach(file => uploadData.append('images', file));

    try {
      const res = await axios_instance.put(`/api/products/${formData._id}/upload-images`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExistingImages(res.data.images);
      showToast("تمت إضافة الصور بنجاح");
    } catch (err) {
      showToast("خطأ في رفع الصور", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!hasChanges || !isFormValid) return;
    setUpdating(true);
    
    const payload = {
      name: { ar: formData.nameAr, en: formData.nameEn, fr: formData.nameFr },
      description: { ar: formData.descAr, en: formData.descEn, fr: formData.descFr },
      detailedDescription: { ar: formData.detailedAr, en: formData.detailedEn, fr: formData.detailedFr },
      category: formData.category,
      subCategory: formData.subCategory,
      isNewProduct: formData.isNewProduct,
      isMostSold: formData.isMostSold,
      featured: formData.featured,
      isPack: formData.isPack,
      status: formData.status,
      variants: variants.map(v => ({
        grammage: v.grammage,
        price: Number(v.price),
        discountPrice: v.isOnSale && v.discountPrice ? Number(v.discountPrice) : 0,
        isOnSale: Boolean(v.isOnSale),
        stock: Number(v.stock)
      })),
      images: existingImages
    };

    try {
      await axios_instance.put(`/api/products/${formData._id}`, payload);
      showToast("تم تحديث المنتج بنجاح");
      setInitialState({
        form: JSON.stringify(formData),
        variants: JSON.stringify(variants),
        images: JSON.stringify(existingImages)
      });
      setTimeout(() => navigate('/admin/products'), 1500);
    } catch (err) {
      showToast(err.response?.data?.message || "خطأ في التحديث", "error");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-20 bg-gray-50 min-h-screen">
      <Loader2 className="animate-spin text-emerald-600" size={40} />
    </div>
  );

  return (
    <div className="p-6 bg-gray-50 min-h-screen relative" dir="rtl">
      
      {notification.show && (
        <div className={`fixed top-10 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl border animate-in fade-in slide-in-from-top-10 duration-300 ${
          notification.type === 'success' ? 'bg-white border-emerald-100 text-emerald-700' : 'bg-white border-red-100 text-red-700'
        }`}>
          {notification.type === 'success' ? <CheckCircle2 size={20} className="text-emerald-500" /> : <AlertCircle size={20} className="text-red-500" />}
          <span className="text-sm font-bold">{notification.message}</span>
          <button onClick={() => setNotification({ ...notification, show: false })} className="mr-4 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-2xl p-8 relative">
        
        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 pb-5 border-b border-gray-100 gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowRight size={22} className="text-gray-500" />
            </button>
            <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600"><Package size={22} /></div>
            <h2 className="text-xl font-black text-gray-800 tracking-tight">تعديل المنتج</h2>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setFormData({...formData, isNewProduct: !formData.isNewProduct})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isNewProduct ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Sparkles size={12} /> NEW
            </button>
            <button type="button" onClick={() => setFormData({...formData, isMostSold: !formData.isMostSold})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isMostSold ? 'bg-orange-500 border-orange-500 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Flame size={12} /> BEST SELLER
            </button>
            <button type="button" onClick={() => setFormData({...formData, featured: !formData.featured})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.featured ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Star size={12} /> FEATURED
            </button>
            <button type="button" onClick={() => setFormData({...formData, isPack: !formData.isPack})}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] font-black border transition-all ${formData.isPack ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white text-gray-400 border-gray-200'}`}>
              <Package size={12} /> PACK
            </button>

            {/* Status selector */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              {[
                { value: 'available', label: 'متوفر', color: 'bg-emerald-500' },
                { value: 'low_stock', label: 'محدود', color: 'bg-orange-400' },
                { value: 'out_of_stock', label: 'نفذ', color: 'bg-red-500' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFormData({...formData, status: opt.value})}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black transition-all ${formData.status === opt.value ? `${opt.color} text-white` : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${formData.status === opt.value ? 'bg-white' : opt.color}`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleUpdate} className="space-y-12">
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelStyle}>الاسم (AR)</label>
              <input type="text" value={formData.nameAr} className={inputStyle} onChange={e => setFormData({...formData, nameAr: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Product Name (EN)</label>
              <input type="text" value={formData.nameEn} className={inputStyle} onChange={e => setFormData({...formData, nameEn: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Nom (FR)</label>
              <input type="text" value={formData.nameFr} className={inputStyle} onChange={e => setFormData({...formData, nameFr: e.target.value})} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
            <div className="space-y-3">
              <label className={labelStyle}>التصنيفات الرئيسية *</label>
              <div className="flex flex-wrap gap-2">
                {categories.filter(c => !c.parentCategory).map(c => (
                  <button key={c._id} type="button" onClick={() => toggleArray('category', c._id)}
                    className={`px-4 py-2 rounded-lg text-[11px] font-bold border transition-all ${formData.category.includes(c._id) ? 'bg-gray-800 border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                    {c.name.ar}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className={labelStyle}>الأقسام الفرعية</label>
              <div className="flex flex-col gap-4 max-h-[250px] overflow-y-auto pr-2">
                {formData.category.length > 0 ? (
                  formData.category.map((mainId) => {
                    const mainCat = categories.find(c => c._id === mainId);
                    const relatedSubs = subCategories.filter(s => (s.parentCategory?._id || s.parentCategory) === mainId);
                    if (relatedSubs.length === 0) return null;
                    return (
                      <div key={mainId} className="bg-white p-3 rounded-xl border border-gray-100">
                        <p className="text-[9px] font-black text-emerald-600 mb-2 uppercase tracking-tighter">{mainCat?.name?.ar}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {relatedSubs.map(s => (
                            <button key={s._id} type="button" onClick={() => toggleArray('subCategory', s._id)}
                              className={`px-3 py-1 rounded-lg text-[10px] font-bold border transition-all ${formData.subCategory.includes(s._id) ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-400 hover:border-emerald-200'}`}>
                              {s.name.ar}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })
                ) : <p className="text-[10px] text-gray-400 italic">حدد تصنيفاً لعرض الفروع</p>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={labelStyle}>الوصف المختصر (AR)</label>
              <textarea value={formData.descAr} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descAr: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Short Description (EN)</label>
              <textarea value={formData.descEn} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descEn: e.target.value})} />
            </div>
            <div dir="ltr">
              <label className={labelStyle}>Description Courte (FR)</label>
              <textarea value={formData.descFr} className={`${inputStyle} h-24 resize-none`} onChange={e => setFormData({...formData, descFr: e.target.value})} />
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-6 text-gray-400 uppercase tracking-widest text-[10px] font-black">
              <Languages size={14} /> الوصف التفصيلي (DETAILED CONTENT)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelStyle}>المحتوى التفصيلي (AR)</label>
                <textarea value={formData.detailedAr} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedAr: e.target.value})} />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Detailed Content (EN)</label>
                <textarea value={formData.detailedEn} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedEn: e.target.value})} />
              </div>
              <div dir="ltr">
                <label className={labelStyle}>Contenu Détaillé (FR)</label>
                <textarea value={formData.detailedFr} className={`${inputStyle} h-44`} onChange={e => setFormData({...formData, detailedFr: e.target.value})} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="bg-gray-50/50 p-6 rounded-2xl border border-gray-100 h-fit">
              <div className="flex justify-between items-center mb-4">
                <span className={labelStyle}>الأوزان والأسعار *</span>
                <button type="button" onClick={() => setVariants([...variants, { grammage: '', price: '', discountPrice: '', isOnSale: false, stock: 0 }])} className="text-emerald-600 flex items-center gap-1 text-[11px] font-bold"><PlusCircle size={14} /> إضافة خيار</button>
              </div>
              {variants.map((v, i) => (
                <div key={i} className="mb-4 p-3 bg-white rounded-xl border border-gray-200">
                  <div className="flex gap-2 mb-2">
                    <input type="text" placeholder="الوزن / الحجم" value={v.grammage} className={inputStyle} onChange={e => { let vts = [...variants]; vts[i].grammage = e.target.value; setVariants(vts); }} />
                    <input type="number" step="0.001" placeholder="السعر الأصلي" value={v.price} className={inputStyle} onChange={e => { let vts = [...variants]; vts[i].price = e.target.value; setVariants(vts); }} />
                    {variants.length > 1 && <button type="button" onClick={() => setVariants(variants.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 px-1 transition-colors"><Trash2 size={16} /></button>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { let vts = [...variants]; vts[i].isOnSale = !vts[i].isOnSale; setVariants(vts); }}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-black border transition-all ${v.isOnSale ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-gray-200 text-gray-400'}`}
                    >% تخفيض</button>
                    {v.isOnSale && (
                      <input type="number" step="0.001" placeholder="السعر بعد التخفيض" value={v.discountPrice} className={inputStyle} onChange={e => { let vts = [...variants]; vts[i].discountPrice = e.target.value; setVariants(vts); }} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center mb-1">
                <label className={labelStyle}>صور المنتج ({existingImages.length}/4)</label>
                <button 
                  type="button" 
                  disabled={uploading || existingImages.length >= 4}
                  onClick={() => fileInputRef.current.click()}
                  className={`text-[10px] font-black uppercase tracking-tighter transition-colors flex items-center gap-1.5 ${existingImages.length >= 4 ? 'text-gray-300 cursor-not-allowed' : 'text-emerald-600 hover:text-emerald-700'}`}
                >
                  {uploading ? <RotateCw size={12} className="animate-spin" /> : <Upload size={12} />} إضافة صور
                </button>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  multiple 
                  className="hidden" 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                />
              </div>
              <div className="border border-gray-200 rounded-2xl p-4 bg-white min-h-[175px]">
                {existingImages.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3">
                    {existingImages.map((img, i) => (
                      <div
                        key={img}
                        draggable
                        onDragStart={() => { dragIndexRef.current = i; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          const from = dragIndexRef.current;
                          if (from === null || from === i) return;
                          const reordered = [...existingImages];
                          const [moved] = reordered.splice(from, 1);
                          reordered.splice(i, 0, moved);
                          setExistingImages(reordered);
                          dragIndexRef.current = null;
                        }}
                        className="relative aspect-square border border-gray-100 rounded-xl overflow-hidden cursor-grab active:cursor-grabbing"
                      >
                        <img
                          src={`${backendURL}/${img}`}
                          className={`w-full h-full object-cover transition-opacity ${deletingImg === img ? 'opacity-20' : 'opacity-100'}`}
                          alt="product"
                        />
                        {/* First image badge */}
                        {i === 0 && (
                          <span className="absolute top-1 start-1 bg-emerald-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md leading-none">
                            1st
                          </span>
                        )}
                        {/* Drag handle */}
                        <div className="absolute top-1 end-1 text-white/80">
                          <GripVertical size={12} />
                        </div>
                        <div className={`absolute inset-0 flex items-center justify-center transition-all bg-black/40 ${confirmDelete === img || deletingImg === img ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}>
                          {deletingImg === img ? (
                            <RotateCw size={20} className="text-white animate-spin" />
                          ) : confirmDelete === img ? (
                            <div className="flex gap-2">
                              <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteImage(img); }} className="bg-emerald-600 text-white p-2 rounded-lg hover:bg-emerald-700"><Check size={14} /></button>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }} className="bg-white text-gray-900 p-2 rounded-lg hover:bg-gray-100"><X size={14} /></button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <a
                                href={`${backendURL}/${img}`}
                                download
                                target="_blank"
                                rel="noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="bg-white text-gray-900 p-2.5 rounded-full hover:scale-110 flex items-center justify-center"
                              >
                                <Download size={16} />
                              </a>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setConfirmDelete(img); }} className="bg-red-600 text-white p-2.5 rounded-full hover:scale-110"><Trash2 size={18} /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-300">
                    <ImageIcon size={32} strokeWidth={1} />
                    <p className="text-[10px] font-black mt-2">لا توجد صور</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button type="submit" disabled={!hasChanges || !isFormValid || updating}
            className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${hasChanges && isFormValid && !updating ? 'bg-gray-900 text-white hover:bg-black' : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'}`}>
            {updating ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {(!hasChanges && isFormValid) ? 'لا توجد تغييرات' : (!isFormValid ? 'يرجى إكمال البيانات' : 'حفظ التعديلات')}</>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;